import type { Image } from 'models';
import { useTranslation } from 'react-i18next';
import {
  type FileUploadDone,
  Form,
  FormActions,
  ImageDraggerWithCrop,
  Modal,
  type UploadChangeParam,
  type UploadedFileList,
  generateUploadFile,
  useGenerateImageUploadRulesAndValidations,
} from 'ui';
import { loadImage } from 'common/utils';
import { uploadImage } from 'features/files';

type FormValues = {
  image: [FileUploadDone];
};

type P = {
  image: Image;
  isLoading?: boolean;
  isOpened: boolean;
  onCancel: () => void;
  onSubmit: (image: Image) => void;
  title: string;
};

export const DEFAULT_COMMUNITY_IMAGE_ID = 'default_community_image_id';

const EditImageModal = ({ image, isLoading, isOpened, onCancel, onSubmit, title }: P) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValues>();

  const { rules: imageRules, validations: imageValidations } = useGenerateImageUploadRulesAndValidations();

  const handleFinish = (values: FormValues) => {
    values.image[0].response.id === DEFAULT_COMMUNITY_IMAGE_ID ? onCancel() : onSubmit(values.image[0].response);
  };

  const initialValues: Partial<FormValues> = {
    image: generateUploadFile({ response: image }),
  };

  return (
    <Modal isOpened={isOpened} maskClosable={false} onCancel={onCancel} title={title} width="medium">
      <Modal.HugContent>
        <Form<FormValues> form={form} initialValues={initialValues} onFinish={handleFinish}>
          <Form.Item<FormValues>
            getValueFromEvent={({ fileList }: UploadChangeParam): UploadedFileList => fileList}
            name="image"
            rules={imageRules}
            tooltip="Members will see this image preview in Channels and My Content."
            valuePropName="fileList"
          >
            <ImageDraggerWithCrop
              dragger={{
                loadImage,
                shape: 'rectangle',
                size: 'large',
                uploadFile: uploadImage,
                validations: imageValidations,
              }}
            />
          </Form.Item>
          <Modal.HugContent.Footer>
            <FormActions submit={{ children: t('Save'), loading: isLoading }} />
          </Modal.HugContent.Footer>
        </Form>
      </Modal.HugContent>
    </Modal>
  );
};

export default EditImageModal;
